import { useCallback } from "react";
import { trpc } from "@/lib/trpc/react";

/**
 * Smart prefetching strategies for the festival platform
 * Predictively loads data based on user behavior patterns
 */

export function useSmartPrefetch() {
	const utils = trpc.useUtils();

	/**
	 * Prefetch festival details when user hovers over festival card
	 * Includes related data likely to be needed
	 */
	const prefetchFestival = useCallback(
		async (slug: string) => {
			// Prefetch multiple related queries in parallel
			await Promise.allSettled([
				utils.festival.getBySlug.prefetch({ slug }),
				// Note: getSets requires festivalId, which we get from getBySlug response
				// So we can't prefetch sets until we have the festival ID
			]);
		},
		[utils],
	);

	/**
	 * Prefetch artist details when user shows interest
	 */
	const prefetchArtist = useCallback(
		async (artistName: string) => {
			await Promise.allSettled([
				utils.artist.getByName.prefetch({ artistName }),
				utils.artist.getProfileWithSets.prefetch({ artistName }),
				// Note: getStats requires artistId, which we get from getByName response
				// So we can't prefetch stats until we have the artist ID
			]);
		},
		[utils],
	);

	/**
	 * Prefetch user profile data when viewing social interactions
	 */
	const prefetchUserProfile = useCallback(
		async (username: string) => {
			await Promise.allSettled([
				utils.user.getProfileByUsername.prefetch({ username }),
				// Note: getStats requires userId, which we get from getProfileByUsername response
				// So we can't prefetch stats until we have the user ID
			]);
		},
		[utils],
	);

	/**
	 * Background sync for critical user data
	 * Keeps important data fresh without user action
	 */
	const backgroundSync = useCallback(() => {
		// Invalidate stale user data
		utils.user.getProfile.invalidate();
		utils.social.getNotifications.invalidate();

		// Refresh frequently changing data
		utils.festival.list.invalidate();
	}, [utils]);

	/**
	 * Predictive prefetch based on current page
	 * Called when user navigates to different sections
	 */
	const prefetchForPage = useCallback(
		async (page: "festivals" | "artists" | "shows" | "profile") => {
			switch (page) {
				case "festivals":
					// Prefetch popular festivals and filters
					await Promise.allSettled([
						utils.festival.list.prefetch({
							limit: 20,
						}),
						utils.festival.getFilters.prefetch(),
					]);
					break;

				case "artists":
					// Prefetch trending artists
					await Promise.allSettled([
						utils.artist.list.prefetch({
							limit: 20,
							sort: "popularity",
						}),
						utils.artist.getGenres.prefetch(),
					]);
					break;

				case "shows":
					// Prefetch upcoming shows
					await Promise.allSettled([
						utils.show.list.prefetch({
							limit: 20,
							tab: "upcoming",
						}),
						utils.show.getFilters.prefetch({}),
					]);
					break;

				case "profile":
					// Note: Profile page prefetching requires specific user context
					// So generic prefetching without userId is not possible
					break;
			}
		},
		[utils],
	);

	return {
		prefetchFestival,
		prefetchArtist,
		prefetchUserProfile,
		backgroundSync,
		prefetchForPage,
	};
}

/**
 * Hook for managing prefetch on hover/focus
 */
export function usePrefetchOnHover() {
	const { prefetchFestival, prefetchArtist, prefetchUserProfile } =
		useSmartPrefetch();

	const createPrefetchHandlers = useCallback(
		(type: "festival" | "artist" | "user", identifier: string) => {
			let timeoutId: NodeJS.Timeout;

			const onMouseEnter = () => {
				// Delay prefetch slightly to avoid excessive requests
				timeoutId = setTimeout(() => {
					switch (type) {
						case "festival":
							prefetchFestival(identifier);
							break;
						case "artist":
							prefetchArtist(identifier);
							break;
						case "user":
							prefetchUserProfile(identifier);
							break;
					}
				}, 100); // 100ms delay for faster response
			};

			const onMouseLeave = () => {
				clearTimeout(timeoutId);
			};

			// Immediate prefetch on touch for mobile
			const onTouchStart = () => {
				switch (type) {
					case "festival":
						prefetchFestival(identifier);
						break;
					case "artist":
						prefetchArtist(identifier);
						break;
					case "user":
						prefetchUserProfile(identifier);
						break;
				}
			};

			return { onMouseEnter, onMouseLeave, onTouchStart };
		},
		[prefetchFestival, prefetchArtist, prefetchUserProfile],
	);

	return { createPrefetchHandlers };
}
